// src/utils/spaceFitVerdict.js
//
// OFFICES-SPACE-FIT-METRIC: Two-lens Space Fit verdict.
//
// Lens 1 — Desk fit: people ÷ seats (how full are the desks?)
// Lens 2 — Build density: SF ÷ seats (how cramped is the building?)
//
// Four plain-language verdicts:
//   Over-Capacity — people exceed installed seats (>100% desk fill)
//   Dense         — SF/seat is below the tight threshold (cramped building)
//   Under-Used    — desks less than 80% occupied (but not cramped)
//   Right-Sized   — balanced desk fill (80–100%) and not density-constrained
//
// Escalation order: Over-Capacity → Dense → Under-Used → Right-Sized.
// Over-Capacity wins over density concerns; density wins over under-use.
//
// SF is NOT required for desk-based verdicts — when SF is absent but
// seats+people are present, Over-Capacity and Under-Used still resolve.
// Dense requires SF data. Right-Sized requires either balanced desks or
// a non-cramped density reading.
//
// This is entirely client-side — no DB reads, no server changes.
// The archived classifySpaceFit / space_fit_knobs / benchmark_bands /
// location_score* code must NOT be touched.

import { T } from "../app/tokens.js";
import { classifySeatFit, FIT_LABEL, isFitBand } from "../services/seatFit.js";
import { fitPolicy } from "../services/spacePolicyValues.js";
import { SPACE_POLICY_SEEDS } from "../services/spacePolicyValues.js";
import { getSpacePolicy } from "../services/spacePolicyStore.js";
import { SPACE_FIT_COLOR as SHARED_COLOR, SPACE_FIT_BG as SHARED_BG,
  SPACE_FIT_BORDER as SHARED_BORDER, SPACE_FIT_HEX as SHARED_HEX } from "../services/officeSeatFitPresentation.js";
import {
  COMFORT_TIGHT_MIN_SF_PER_SEAT,
  COMFORT_ROOMY_MIN_SF_PER_SEAT,
} from "./comfortDensity.js";

// ── Desk-fill thresholds ──────────────────────────────────────────────────────

export const DESK_OVER_THRESHOLD    = 1.0;  // > 100% → Over-Capacity
export const DESK_BALANCED_MIN      = SPACE_POLICY_SEEDS.underUsedPct / 100; // Seed-only compatibility; live readers use effective policy.
// < 80% → Under-Used

// ── Verdict strings (exported for callers that need exact matching) ───────────

export const SPACE_FIT_VERDICT_OVER  = "Over-Cap";
export const SPACE_FIT_VERDICT_TIGHT = "Tight";
export const SPACE_FIT_VERDICT_DENSE = "Dense";
export const SPACE_FIT_VERDICT_UNDER = "Under-Used";
export const SPACE_FIT_VERDICT_RIGHT = "Right-Sized";

// ── Short/full display labels ─────────────────────────────────────────────────
// Single source of truth for both spellings of each verdict tier.
// Use .short in tight-column pills; use .full everywhere else (drawer, report,
// map legend, scatter legend). For Dense / Under-Used / Right-Sized the two
// spellings are identical — only Over-Capacity needs an abbreviation.

export const SPACE_FIT_LABEL = {
  [SPACE_FIT_VERDICT_OVER]:  { full: "Over-Cap", short: "Over-Cap" },
  [SPACE_FIT_VERDICT_TIGHT]: { full: "Tight", short: "Tight" },
  [SPACE_FIT_VERDICT_DENSE]: { full: "Dense",         short: "Dense"         },
  [SPACE_FIT_VERDICT_UNDER]: { full: "Under-Used",    short: "Under-Used"    },
  [SPACE_FIT_VERDICT_RIGHT]: { full: "Right-Sized",   short: "Right-Sized"   },
};

// ── Short action labels (Task #9099) ─────────────────────────────────────────
// One mapping, adjacent to the verdict logic, for compact surfaces such as the
// record-drawer header strip. The long recommendation stays in the detailed
// Space fit card (VERDICT_RECOMMENDED). Unknown/missing verdicts get the
// explicit "needs data" label, never an invented action.

export const SPACE_FIT_SHORT_ACTION = {
  [SPACE_FIT_VERDICT_TIGHT]: "Review capacity",
  [SPACE_FIT_VERDICT_RIGHT]: "Monitor",
  [SPACE_FIT_VERDICT_UNDER]: "Review footprint",
  [SPACE_FIT_VERDICT_DENSE]: "Relieve density",
  [SPACE_FIT_VERDICT_OVER]:  "Add seats or rebalance",
};
export const SPACE_FIT_SHORT_ACTION_UNKNOWN = "Needs seats and headcount";

export function spaceFitShortAction(verdict) {
  return (verdict && SPACE_FIT_SHORT_ACTION[verdict]) || SPACE_FIT_SHORT_ACTION_UNKNOWN;
}

// ── Severity order (lower = more severe; blanks always last in sort) ──────────

export const SPACE_FIT_SEVERITY = {
  [SPACE_FIT_VERDICT_TIGHT]: 1,
  [SPACE_FIT_VERDICT_OVER]:  0,
  [SPACE_FIT_VERDICT_DENSE]: 1,
  [SPACE_FIT_VERDICT_UNDER]: 2,
  [SPACE_FIT_VERDICT_RIGHT]: 3,
};

// ── Color map — V2 CSS variable tokens ───────────────────────────────────────
// Key: space_fit_verdict (internal). Used by list pills, tile pills, map markers,
// map sidebar dots, and cluster icons.

export const SPACE_FIT_COLOR = {
  ...SHARED_COLOR,
  [SPACE_FIT_VERDICT_DENSE]: T.mid,
  null:                       "var(--v2-light)",
};

export const SPACE_FIT_BG = {
  ...SHARED_BG,
  [SPACE_FIT_VERDICT_DENSE]: T.card,
  null:                       "var(--v2-hair)",
};

export const SPACE_FIT_BORDER = {
  ...SHARED_BORDER,
  [SPACE_FIT_VERDICT_DENSE]: T.borderDark,
  null:                       "var(--v2-line)",
};

// Hex literals for environments where CSS variables cannot resolve
// (e.g. Leaflet divIcon innerHTML strings). Uses T tokens so all values
// stay in sync with src/app/tokens.js.
export const SPACE_FIT_HEX = {
  ...SHARED_HEX,
  [SPACE_FIT_VERDICT_DENSE]: T.mid,
  null:                       T.light,
};

// ── Shared people-vs-seats delta helper ──────────────────────────────────────

/**
 * Compute the raw people-vs-seats delta for a location.
 *
 * Returns `headcount − seats` (positive = deficit, negative/zero = surplus).
 * Returns null when either input is missing — callers must treat null as
 * "no data" and exclude the location from people-based bucketing.
 *
 * This is the single source of truth shared by the Space Fit report's
 * "Desk-fit" column and the Pulse "Space pipeline" bucketing logic so both
 * surfaces produce identical figures.
 *
 * @param {number|null} seats    Installed seats (loc.installedCapacity).
 * @param {number|null} headcount Latest headcount total. May be null.
 * @returns {number|null}  people − seats, or null when data is absent.
 */
export function computePeopleDelta(seats, headcount) {
  if (seats == null || headcount == null) return null;
  const s = Number(seats);
  const h = Number(headcount);
  if (!Number.isFinite(s) || !Number.isFinite(h)) return null;
  return h - s;
}

// ── Core computation ──────────────────────────────────────────────────────────

/**
 * Compute the Space Fit verdict for a location.
 *
 * Bands always use assigned headcount / installed seats and effective company
 * guardrails. SF never creates or replaces a band. Dense is an independent flag;
 * missing assignment remains unscored even when physical density is known.
 *
 * @param {number|null} derivedSf  Derived rentable SF from getDisplayedSqft (never location.sqft directly).
 * @param {number|null} seats      Installed seats (loc.installedCapacity). Missing → verdict null.
 * @param {number|null} people     Latest headcount total (loc.latestHeadcount?.totalCount). May be null.
 * @returns {{
 *   verdict: string|null,   // SPACE_FIT_VERDICT_* or null
 *   densityBand: string|null, // "roomy"|"comfortable"|"cramped"|null (null when sf missing)
 *   deskBand: string|null,    // "over"|"balanced"|"under"|null (null when people missing)
 *   deskDelta: number|null,   // people - seats (null when people missing)
 * }}
 */
export function computeSpaceFit(derivedSf, seats, people, policy = getSpacePolicy()) {
  const number = value => value == null || typeof value === "boolean" || String(value).trim() === "" ? null : Number(value);
  const seatCount = number(seats);

  // Guard: seats required for any verdict
  if (seatCount == null || !Number.isFinite(seatCount) || seatCount < 0) {
    return { verdict: null, densityBand: null, deskBand: null, deskDelta: null };
  }

  const sf = number(derivedSf);
  const peopleCount = number(people);

  // Desk band (fill ratio) — does NOT require SF
  let deskBand = null;
  let deskDelta = null;
  if (peopleCount != null && Number.isFinite(peopleCount)) {
    deskDelta = peopleCount - seatCount;
    const fill = peopleCount / seatCount;
    if (fill > DESK_OVER_THRESHOLD) deskBand = "over";
    else if (fitPolicy(policy) && fill >= fitPolicy(policy).underUsedPct / 100) deskBand = "balanced";
    else deskBand = "under";
  }

  // Density band (SF per seat) — only when SF is present and positive
  let densityBand = null;
  if (sf != null && Number.isFinite(sf) && sf > 0 && seatCount > 0) {
    const ratio = sf / seatCount;
    if (ratio >= COMFORT_ROOMY_MIN_SF_PER_SEAT) densityBand = "roomy";
    else if (fitPolicy(policy) && ratio >= fitPolicy(policy).sfFloor) densityBand = "comfortable";
    else densityBand = "cramped";
  }

  const status = classifySeatFit(peopleCount, seatCount, policy);
  const dense = !!fitPolicy(policy) && sf > 0 && seatCount > 0 && sf / seatCount < fitPolicy(policy).sfFloor;
  return { status, verdict: isFitBand(status) ? FIT_LABEL[status] : null, dense, densityBand, deskBand, deskDelta };
}

export function computeMarketSpaceFit(market, policy = getSpacePolicy()) {
  const source = market.spaceFitToday;
  if (source && source.status !== "ok") return { verdict: null, dense: false, status: source.status };
  const result = computeSpaceFit(market.totalDerivedSqft,
    source ? source.seats?.value : market.totalSeats,
    source ? source.assigned?.value : market.totalPeople, policy);
  return { ...result, partial: !!(source?.assigned?.partial || source?.seats?.partial) };
}
